import { loadCKEditorCloud } from '@ckeditor/ckeditor5-react';

const {
  CKEditor: { Plugin },
  CKEditorPremiumFeatures: { RevisionHistory, RevisionTracker },
} = await loadCKEditorCloud({
  version: '44.3.0',
  premium: true,
  translations: ['en'],
});

type TRevisionHistory = InstanceType<typeof RevisionHistory>;
type TRevisionTracker = InstanceType<typeof RevisionTracker>;
type TRevisionJSON = Parameters<TRevisionHistory['addRevisionData']>[0];
type TRevisionTrackerWithCurrent = TRevisionTracker & {
  currentRevision: TRevisionJSON;
};

export class RevisionHistoryAutosaveIntegration extends Plugin {
  private _saveAfter!: number;

  private _autosaveCount!: number;

  private _lastCreatedAt!: Date | null;

  private _lastRevisionTime!: Date;

  private _timeThreshold!: number;

  private _lastRevisionData!: string | null;

  private _contentChangeThreshold!: number;

  private _isRevisionUpdatesEnabled!: boolean;

  init() {
    this._saveAfter = 100; // Create a new revision after 100 saves.
    this._autosaveCount = 1; // Current autosave counter.
    this._lastCreatedAt = null; // Revision `createdAt` value, when the revision was last autosaved.
    this._lastRevisionTime = new Date(); // Time when the last revision was created
    this._timeThreshold = 5 * 60 * 1000; // 5 minutes in milliseconds
    this._lastRevisionData = null; // Content of the last revision
    this._contentChangeThreshold = 0.1; // 10% change threshold
    this._isRevisionUpdatesEnabled = true;
  }

  private getRevisionTracker(): TRevisionTrackerWithCurrent {
    return this.editor.plugins.get(
      RevisionTracker,
    ) as unknown as TRevisionTrackerWithCurrent;
  }

  private shouldCreateNewRevision(currentContent: string): boolean {
    const now = new Date();

    // Check if 5 minutes have passed since the last revision
    if (
      now.getTime() - this._lastRevisionTime.getTime() >=
      this._timeThreshold
    ) {
      return true;
    }

    // Check if we've reached 100 edits
    if (this._autosaveCount >= this._saveAfter) {
      return true;
    }

    // Check for significant content change if we have previous content
    if (this._lastRevisionData) {
      // Simple strategy: check if content length has changed by threshold percentage
      const prevLength = this._lastRevisionData.length;
      const currentLength = currentContent.length;

      if (prevLength > 0) {
        const changeRatio = Math.abs(currentLength - prevLength) / prevLength;
        if (changeRatio >= this._contentChangeThreshold) {
          return true;
        }
      }
    }

    return false;
  }

  private getCurrentContent(): string {
    return this.editor.getData();
  }

  private resetTracker(
    currentRevision: TRevisionJSON,
    currentContent: string,
  ): void {
    this._autosaveCount = 1;
    this._lastCreatedAt = currentRevision.createdAt;
    this._lastRevisionTime = new Date();
    this._lastRevisionData = currentContent;
  }

  private updateTrackerState(currentRevision: TRevisionJSON): void {
    if (
      !this._lastCreatedAt ||
      currentRevision.createdAt > this._lastCreatedAt
    ) {
      this._autosaveCount += 1;
      this._lastCreatedAt = currentRevision.createdAt;
    }
  }

  async autosave() {
    const revisionTracker = this.getRevisionTracker();
    const { currentRevision } = revisionTracker;
    const currentContent = this.getCurrentContent();

    if (
      !this._lastCreatedAt ||
      currentRevision.createdAt > this._lastCreatedAt
    ) {
      this._autosaveCount = 1;
    }

    if (!this._isRevisionUpdatesEnabled) {
      return false;
    }

    if (this.shouldCreateNewRevision(currentContent)) {
      await revisionTracker.saveRevision();
      this.resetTracker(currentRevision, currentContent);
    } else {
      await revisionTracker.update();
      this.updateTrackerState(currentRevision);
    }

    return true;
  }

  public disableRevisionUpdates() {
    this._isRevisionUpdatesEnabled = false;
  }

  public enableRevisionUpdates() {
    this._isRevisionUpdatesEnabled = true;
  }
}
